import { useEffect, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { trackEvent } from '../../../helpers/analytics';
import usePageHook from '../../../hooks/usePageHook';
import { userSelector } from '../../../redux/selectors';
import { useGetSharedListBySlugQuery } from '../../../services/sharedLists';
import { useGetMfaStatusQuery } from '../../../services/user';

export const useSharedCompanyList = () => {
  const { slug } = useParams();
  const user = useSelector(userSelector);
  const { pathname } = useLocation();
  const [isUserOnboarded, setIsUserOnboarded] = useState(
    !!user.email && !!user.confirmed && user.linkedinSync != null && !pathname.includes('share-contacts'),
  );
  const { data, isLoading: isFetchingPreview, error } = useGetSharedListBySlugQuery({ slug });
  // Needs a session, so skip until the account exists. Gate on the resolved signal only: a
  // failed status fetch falls through here instead of trapping the user on /mfa — backend
  // session enforcement still forces enrollment on their next sign-in. Once enrolled,
  // `enabled` is true so the gate never re-fires (the query usually resolves well before the
  // gate point, which is several steps after sign-up).
  const { data: mfaStatus, isLoading: isMfaStatusLoading } = useGetMfaStatusQuery(undefined, { skip: !user?.id });
  const mfaPending = !!mfaStatus?.requiredByNetwork && !mfaStatus?.enabled;
  // Account exists but the gate's input isn't known yet. Hold past-account routing until it
  // resolves so a required ally can't briefly slip to install-extension before mfaPending flips.
  const mfaStatusUnresolved = !!user?.id && isMfaStatusLoading;
  const history = useHistory();
  const isSyncActive = user?.linkedinSync?.syncStatus === 'active' && !!user?.linkedinSync?.contactsImportInProgress;

  usePageHook(data?.sharedList?.name, { networkName: data?.sharedList?.collection?.name });

  useEffect(() => {
    if (!user?.email && !pathname.includes('user')) {
      history.push(`/shared-company-lists/${slug}/user`);
    } else if (!!user.email && !user.confirmed) {
      history.push(`/shared-company-lists/${slug}/verify-email`);
    } else if (!!user.email && !user.onboardingCompleted) {
      history.push(`/shared-company-lists/${slug}/user/profile`);
    } else if (!!user.email && user.confirmed && user.onboardingCompleted && mfaPending && !pathname.includes('mfa')) {
      // Account is set up but the network requires 2FA and the ally hasn't enrolled — hold
      // them on the in-flow MFA step before any later step. Once enrolled, mfaPending flips
      // false and the step itself routes forward; the `mfa` guards below keep this effect
      // from yanking the user off the step (e.g. while saving backup codes).
      history.push(`/shared-company-lists/${slug}/mfa`);
    } else if (
      !!user.email &&
      user.linkedinSync == null &&
      !mfaStatusUnresolved &&
      !pathname.includes('install-extension') &&
      !pathname.includes('share-contacts') &&
      !pathname.includes('user') &&
      !pathname.includes('mfa')
    ) {
      history.push(`/shared-company-lists/${slug}/install-extension`);
    } else if (
      !!user.email &&
      !!user.linkedinSync &&
      !!user.confirmed &&
      !isUserOnboarded &&
      !mfaStatusUnresolved &&
      !pathname.includes('share-contacts') &&
      !pathname.includes('mfa')
    ) {
      history.push(`/shared-company-lists/${slug}`);
      setIsUserOnboarded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, mfaPending, mfaStatusUnresolved]);

  useEffect(() => {
    if (data?.sharedList) {
      trackEvent('shared_list:view_shared_list', {
        collectionId: data?.sharedList?.collection?.id,
        listSlug: data?.sharedList?.slug,
        objectType: 'companies',
        viewMode: 'table',
      });
    }
  }, [data?.sharedList]);

  return {
    sharedList: data?.sharedList,
    sharedListError: error,
    user,
    isFetchingPreview,
    isUserSignedIn: !!user.email,
    isSyncActive,
    isUserOnboarded,
    setIsUserOnboarded,
  };
};
